import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { CoachSessionDebrief } from '@/types/coach';

interface Props {
  sessionId: string;
  onDebriefReady: (debrief: CoachSessionDebrief) => void;
  onCancel: () => void;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function DebriefRecorder({ sessionId, onDebriefReady, onCancel }: Props) {
  const recorder = useAudioRecorder();
  const [phase, setPhase] = useState<'idle' | 'recording' | 'transcribing' | 'structuring'>('idle');

  useEffect(() => {
    if (recorder.recording && phase === 'idle') setPhase('recording');
  }, [recorder.recording, phase]);

  const tooLongSoon = recorder.durationSec >= 240;

  async function handleStop() {
    setPhase('transcribing');
    const blob = await recorder.stop();

    if (!blob || blob.size < 1024) {
      toast.error('Aufnahme zu kurz.');
      recorder.reset();
      setPhase('idle');
      return;
    }

    let transcript: string;
    try {
      const res = await api.coach.sessions.transcribe(blob);
      transcript = res.transcript.trim();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Transkription fehlgeschlagen.');
      setPhase('idle');
      return;
    }

    if (!transcript) {
      toast.error('Whisper hat keinen Text erkannt. Versuch\'s nochmal oder fülle manuell aus.');
      setPhase('idle');
      return;
    }

    setPhase('structuring');
    try {
      const debrief = await api.coach.sessions.structure(sessionId, transcript);
      onDebriefReady(debrief);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'KI-Strukturierung fehlgeschlagen.');
      setPhase('idle');
    }
  }

  async function handleCancel() {
    await recorder.stop();
    recorder.reset();
    setPhase('idle');
    onCancel();
  }

  if (phase === 'transcribing' || phase === 'structuring') {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-sm">
            {phase === 'transcribing' ? 'Transkribiere mit Whisper…' : 'Strukturiere mit KI…'}
          </span>
        </div>
      </div>
    );
  }

  if (phase === 'recording') {
    return (
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Aufnahme läuft … {formatTime(recorder.durationSec)}
          </span>
          {tooLongSoon && (
            <span className="text-xs text-amber-600">
              Lang — kürzere Etappen empfohlen
            </span>
          )}
        </div>

        <div className="rounded-md border border-input bg-muted/30 p-3 text-sm min-h-[80px]">
          {recorder.liveTranscript || (
            <span className="text-muted-foreground italic">
              {recorder.isSupported
                ? 'Live-Transkript erscheint hier während du sprichst…'
                : 'Aufnahme läuft (Live-Transkript nicht unterstützt).'}
            </span>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={handleStop} className="gap-1.5">
            <Square size={14} /> Stop & Strukturieren
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      {recorder.error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {recorder.error}
        </div>
      )}
      <div className="text-center">
        <Button
          size="lg"
          onClick={() => recorder.start().then(() => setPhase('recording'))}
          disabled={!recorder.isSupported}
          className="gap-2"
        >
          <Mic size={16} /> Mit KI nachbereiten
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          {recorder.isSupported
            ? 'Drücke den Button und sprich frei drauf los.'
            : 'Dein Browser unterstützt keine Sprachaufnahme.'}
        </p>
      </div>
    </div>
  );
}
